/**
 * CHS - Global uppercase enforcement for WCPA stick name fields
 *
 * Supports English and French:
 * - "Name to be printed on your stick"
 * - "Nom à imprimer sur votre bâton"
 *
 * Forces uppercase:
 * - while typing
 * - on paste
 * - on blur/change
 * - before form submit
 * - before AJAX cart/add requests
 * - before FormData serialization
 *
 * This makes the saved cart, checkout, and order value uppercase.
 */

(function () {
  'use strict';

  var TARGET_LABELS = [
    'name to be printed on your stick',
    'name printed on your stick',
    'printed on your stick',

    'nom à imprimer sur votre bâton',
    'nom a imprimer sur votre baton',
    'nom à imprimer',
    'nom a imprimer',
    'nom imprimé',
    'nom imprime'
  ];

  var TARGET_PLACEHOLDERS = [
    'enter name',
    'nom'
  ];

  var NativeFormData = window.FormData;
  var nativeFetch = window.fetch;
  var nativeXHRSend = window.XMLHttpRequest && window.XMLHttpRequest.prototype.send;
  var nativeFormSubmit = window.HTMLFormElement && window.HTMLFormElement.prototype.submit;

  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  function toUppercase(value) {
    return String(value || '').toLocaleUpperCase('fr-CA');
  }

  function containsAny(value, keywords) {
    var normalizedValue = normalize(value);

    if (!normalizedValue) {
      return false;
    }

    return keywords.some(function (keyword) {
      return normalizedValue.indexOf(normalize(keyword)) !== -1;
    });
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value || '').replace(/["\\]/g, '\\$&');
  }

  function isTextInput(input) {
    if (!input || !input.matches) {
      return false;
    }

    return input.matches('input[type="text"], input:not([type]), textarea');
  }

  function getWrapper(input) {
    if (!input || !input.closest) {
      return null;
    }

    return input.closest('.wcpa_field_wrap, .wcpa_row, form');
  }

  function getLabelText(input) {
    if (!input) {
      return '';
    }

    if (input.id) {
      var directLabel = document.querySelector('label[for="' + escapeSelector(input.id) + '"]');

      if (directLabel) {
        return directLabel.textContent || '';
      }
    }

    var wrapper = getWrapper(input);

    if (!wrapper) {
      return '';
    }

    var wrapperLabel = wrapper.querySelector('label');

    return wrapperLabel ? wrapperLabel.textContent || '' : '';
  }

  function isWcpaInput(input) {
    return Boolean(
      input &&
      (
        input.classList.contains('wcpa_field') ||
        input.closest('.wcpa_field_wrap') ||
        input.closest('.wcpa_row')
      )
    );
  }

  function isGeneratedWcpaNameField(input) {
    if (!input || !input.id) {
      return false;
    }

    return /^field_text_/i.test(input.id) || /^field_wcpa-text-/i.test(input.id);
  }

  function isTargetInput(input) {
    if (!isTextInput(input) || !isWcpaInput(input)) {
      return false;
    }

    var labelText = getLabelText(input);
    var placeholder = input.getAttribute('placeholder') || '';
    var name = input.getAttribute('name') || '';
    var id = input.id || '';
    var wrapper = getWrapper(input);
    var wrapperId = wrapper ? wrapper.id || '' : '';

    if (containsAny(labelText, TARGET_LABELS)) {
      return true;
    }

    if (
      isGeneratedWcpaNameField(input) &&
      containsAny(placeholder, TARGET_PLACEHOLDERS) &&
      containsAny(labelText, TARGET_LABELS)
    ) {
      return true;
    }

    if (
      containsAny(name, TARGET_LABELS) ||
      containsAny(id, TARGET_LABELS) ||
      containsAny(wrapperId, TARGET_LABELS)
    ) {
      return true;
    }

    return false;
  }

  function markTargetInputs(root) {
    var scope = root && root.querySelectorAll ? root : document;

    scope
      .querySelectorAll('input[type="text"], input:not([type]), textarea')
      .forEach(function (input) {
        if (isTargetInput(input)) {
          input.classList.add('chs-force-uppercase-stick-name');
        }
      });
  }

  function uppercaseInput(input, dispatchEvents) {
    if (!isTargetInput(input)) {
      return;
    }

    input.classList.add('chs-force-uppercase-stick-name');

    var oldValue = input.value || '';
    var newValue = toUppercase(oldValue);

    if (oldValue === newValue) {
      return;
    }

    var selectionStart = input.selectionStart;
    var selectionEnd = input.selectionEnd;

    input.value = newValue;
    input.setAttribute('value', newValue);

    try {
      if (
        document.activeElement === input &&
        typeof selectionStart === 'number' &&
        typeof selectionEnd === 'number'
      ) {
        input.setSelectionRange(selectionStart, selectionEnd);
      }
    } catch (error) {
      // Safe to ignore.
    }

    if (dispatchEvents && !input.dataset.chsUppercaseDispatching) {
      input.dataset.chsUppercaseDispatching = '1';

      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));

      delete input.dataset.chsUppercaseDispatching;
    }
  }

  function uppercaseAll(root, dispatchEvents) {
    var scope = root && root.querySelectorAll ? root : document;

    markTargetInputs(scope);

    scope
      .querySelectorAll('input[type="text"], input:not([type]), textarea')
      .forEach(function (input) {
        uppercaseInput(input, dispatchEvents);
      });
  }

  function getTargetTokens() {
    var tokens = [];

    document
      .querySelectorAll('input[type="text"], input:not([type]), textarea')
      .forEach(function (input) {
        if (!isTargetInput(input)) {
          return;
        }

        var wrapper = getWrapper(input);
        var labelText = getLabelText(input);

        [
          input.id,
          input.name,
          wrapper ? wrapper.id : '',
          labelText,
          input.id ? input.id.replace(/^field_/i, '') : ''
        ].forEach(function (token) {
          if (token && tokens.indexOf(token) === -1) {
            tokens.push(token);
          }
        });
      });

    TARGET_LABELS.forEach(function (label) {
      if (tokens.indexOf(label) === -1) {
        tokens.push(label);
      }
    });

    return tokens;
  }

  function keyMatchesTarget(key) {
    var normalizedKey = normalize(key);

    if (!normalizedKey) {
      return false;
    }

    if (containsAny(normalizedKey, TARGET_LABELS)) {
      return true;
    }

    return getTargetTokens().some(function (token) {
      var normalizedToken = normalize(token);

      return normalizedToken && normalizedKey.indexOf(normalizedToken) !== -1;
    });
  }

  function objectHasTargetContext(object) {
    if (!object || typeof object !== 'object') {
      return false;
    }

    return [
      object.label,
      object.name,
      object.title,
      object.key,
      object.id,
      object.field,
      object.field_id,
      object.field_name,
      object.field_label,
      object.property
    ].some(function (value) {
      return keyMatchesTarget(value);
    });
  }

  function uppercaseObjectPayload(payload) {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    if (Array.isArray(payload)) {
      payload.forEach(function (item) {
        uppercaseObjectPayload(item);
      });

      return payload;
    }

    var hasTargetContext = objectHasTargetContext(payload);

    Object.keys(payload).forEach(function (key) {
      var value = payload[key];
      var normalizedKey = normalize(key);

      if (typeof value === 'string') {
        if (
          keyMatchesTarget(key) ||
          (
            hasTargetContext &&
            [
              'value',
              'field_value',
              'option_value',
              'property_value'
            ].indexOf(normalizedKey) !== -1
          )
        ) {
          payload[key] = toUppercase(value);
        }

        return;
      }

      if (value && typeof value === 'object') {
        uppercaseObjectPayload(value);
      }
    });

    return payload;
  }

  function uppercaseJsonString(value) {
    if (typeof value !== 'string') {
      return value;
    }

    var trimmed = value.trim();

    if (!trimmed) {
      return value;
    }

    var looksJson =
      (trimmed.charAt(0) === '{' && trimmed.charAt(trimmed.length - 1) === '}') ||
      (trimmed.charAt(0) === '[' && trimmed.charAt(trimmed.length - 1) === ']');

    if (!looksJson) {
      return value;
    }

    try {
      var parsed = JSON.parse(value);
      uppercaseObjectPayload(parsed);
      return JSON.stringify(parsed);
    } catch (error) {
      return value;
    }
  }

  function uppercaseFormData(formData) {
    if (!NativeFormData || !(formData instanceof NativeFormData)) {
      return formData;
    }

    Array.from(formData.keys()).forEach(function (key) {
      var values = formData.getAll(key);
      var shouldUppercase = keyMatchesTarget(key);

      formData.delete(key);

      values.forEach(function (value) {
        if (typeof value === 'string') {
          formData.append(
            key,
            shouldUppercase ? toUppercase(value) : uppercaseJsonString(value)
          );
        } else {
          formData.append(key, value);
        }
      });
    });

    return formData;
  }

  function uppercaseURLSearchParams(params) {
    if (!(params instanceof URLSearchParams)) {
      return params;
    }

    Array.from(params.keys()).forEach(function (key) {
      var values = params.getAll(key);
      var shouldUppercase = keyMatchesTarget(key);

      params.delete(key);

      values.forEach(function (value) {
        params.append(
          key,
          shouldUppercase ? toUppercase(value) : uppercaseJsonString(value)
        );
      });
    });

    return params;
  }

  function uppercaseBody(body) {
    uppercaseAll(document, true);

    if (!body) {
      return body;
    }

    if (NativeFormData && body instanceof NativeFormData) {
      return uppercaseFormData(body);
    }

    if (body instanceof URLSearchParams) {
      return uppercaseURLSearchParams(body);
    }

    if (typeof body === 'string') {
      var trimmed = body.trim();

      var looksJson =
        (trimmed.charAt(0) === '{' && trimmed.charAt(trimmed.length - 1) === '}') ||
        (trimmed.charAt(0) === '[' && trimmed.charAt(trimmed.length - 1) === ']');

      if (looksJson) {
        try {
          var parsed = JSON.parse(body);
          uppercaseObjectPayload(parsed);
          return JSON.stringify(parsed);
        } catch (error) {
          return body;
        }
      }

      try {
        var params = new URLSearchParams(body);
        uppercaseURLSearchParams(params);
        return params.toString();
      } catch (error) {
        return body;
      }
    }

    if (typeof body === 'object') {
      return uppercaseObjectPayload(body);
    }

    return body;
  }

  function syncFromEvent(event) {
    var target = event && event.target ? event.target : null;
    var form = target && target.closest ? target.closest('form') : null;

    uppercaseAll(form || document, true);
  }

  function injectStyle() {
    if (document.getElementById('chs-force-uppercase-stick-name-style')) {
      return;
    }

    var style = document.createElement('style');

    style.id = 'chs-force-uppercase-stick-name-style';
    style.textContent = [
      '.chs-force-uppercase-stick-name {',
      '  text-transform: uppercase !important;',
      '}'
    ].join('\n');

    document.head.appendChild(style);
  }

  /**
   * Patch FormData.
   * This is important because many Shopify product option apps read the form
   * before the normal submit event finishes.
   */
  if (NativeFormData && !window.__chsStickNameUppercaseFormDataPatched) {
    window.__chsStickNameUppercaseFormDataPatched = true;

    window.FormData = new Proxy(NativeFormData, {
      construct: function (Target, args) {
        var form = args && args[0] && args[0].nodeType === 1 ? args[0] : document;

        uppercaseAll(form, true);

        var formData = Reflect.construct(Target, args);

        return uppercaseFormData(formData);
      },

      apply: function (Target, thisArg, args) {
        var form = args && args[0] && args[0].nodeType === 1 ? args[0] : document;

        uppercaseAll(form, true);

        var formData = Reflect.apply(Target, thisArg, args);

        return uppercaseFormData(formData);
      }
    });

  }

  /**
   * Patch fetch AJAX.
   */
  if (nativeFetch && !window.__chsStickNameUppercaseFetchPatched) {
    window.__chsStickNameUppercaseFetchPatched = true;

    window.fetch = function (resource, init) {
      if (init && init.body) {
        init.body = uppercaseBody(init.body);
      } else {
        uppercaseAll(document, true);
      }

      return nativeFetch.apply(this, arguments);
    };
  }

  /**
   * Patch XHR AJAX.
   */
  if (nativeXHRSend && !window.__chsStickNameUppercaseXHRPatched) {
    window.__chsStickNameUppercaseXHRPatched = true;

    window.XMLHttpRequest.prototype.send = function (body) {
      return nativeXHRSend.call(this, uppercaseBody(body));
    };
  }

  /**
   * Patch direct form.submit().
   */
  if (nativeFormSubmit && !window.__chsStickNameUppercaseSubmitPatched) {
    window.__chsStickNameUppercaseSubmitPatched = true;

    window.HTMLFormElement.prototype.submit = function () {
      uppercaseAll(this, true);
      return nativeFormSubmit.call(this);
    };
  }

  /**
   * Input timing protection.
   */
  [
    'beforeinput',
    'input',
    'keyup',
    'change',
    'blur',
    'focusout',
    'paste',
    'compositionend'
  ].forEach(function (eventName) {
    document.addEventListener(
      eventName,
      function (event) {
        var input = event.target;

        uppercaseInput(input, false);

        window.setTimeout(function () {
          uppercaseInput(input, true);
        }, 0);

        window.setTimeout(function () {
          uppercaseInput(input, true);
        }, 40);
      },
      true
    );
  });

  /**
   * Add-to-cart timing protection.
   */
  [
    'pointerdown',
    'mousedown',
    'touchstart',
    'click'
  ].forEach(function (eventName) {
    document.addEventListener(
      eventName,
      function (event) {
        var target = event.target;

        if (!target || !target.closest) {
          return;
        }

        var button = target.closest(
          'button[type="submit"], input[type="submit"], [name="add"], .product-form__submit, .shopify-payment-button__button, [data-add-to-cart], [data-cart-add]'
        );

        if (!button) {
          return;
        }

        syncFromEvent(event);
      },
      true
    );
  });

  /**
   * Native submit protection.
   */
  document.addEventListener(
    'submit',
    function (event) {
      syncFromEvent(event);
    },
    true
  );

  /**
   * Dynamic WCPA fields.
   */
  var observer = new MutationObserver(function () {
    uppercaseAll(document, false);
  });

  function init() {
    injectStyle();
    uppercaseAll(document, false);

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
